import React, { useState, useEffect, Suspense } from "react";

import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { DesignPage } from "common/views/DesignPage";
import { ModalProvider } from "common/components/Wrapper/ModalProvider";
import { modalRef } from "common/utils/modalController"; // 경로 확인

import "./App.css";
import "common/css/common.css";
import "common/css/reset.css";
import "common/css/style.css";

import { ErrorScreen } from "common/views";
import { SideBar } from "common/components";
import { Header } from "common/components/Header/header";
import VerifyPasswordScreen from "pages/Login/VerifyPasswordScreen";
import { GlobalProvider } from "common/data/GlobalContext";
import { MobilePage } from "common/views/MobilePage";
import { SalesChartScreen } from "pages/Sales/SalesChartScreen";
import { SalesDetailListScreen } from "pages/Sales/SalesDetailListScreen";
import { ProtectedRoute } from "common/components/Etc/ProtectedRoute";
import { useAccessManager } from "common/hooks/useAccessManager";
import { AccessProvider } from "common/components/Etc/AccessContext";
import { PanelProvider } from "common/utils/panelController";

// Lazy loading for components
// const LoginScreen = React.lazy(() => import("pages/Login/LoginScreen"));
const ResetPwScreen = React.lazy(() => import("pages/Login/ResetPwScreen"));
const ToResetPwScreen = React.lazy(() => import("pages/Login/ToResetPwScreen"));
const SelectTypeScreen = React.lazy(
  () => import("pages/Login/SelectTypeScreen"),
);
const VerifiedEmail = React.lazy(() => import("pages/Login/VerifiedEmail"));

const SignUpBusinessScreen = React.lazy(
  () => import("pages/SignUp/SignUpBusinessScreen"),
);
const SignUpEmployeeScreen = React.lazy(
  () => import("pages/SignUp/SignUpEmployeeScreen"),
);

const WelcomeMapScreen = React.lazy(() => import("pages/Map/WelcomeMapScreen"));
const MyProfileScreen = React.lazy(
  () => import("pages/Profile/MyProfileScreen"),
);

const EmailVerificationScreen = React.lazy(
  () => import("pages/Home/HomeErrorScreen"),
);
const HomeErrorScreen = React.lazy(() => import("pages/Home/HomeErrorScreen"));
const HomeScreen = React.lazy(() => import("pages/Home/HomeScreen"));

const GolfInfoScreen = React.lazy(
  () => import("pages/Information/GolfInfoScreen"),
);
const ClientListScreen = React.lazy(
  () => import("pages/Client/ClientListScreen"),
);
const ClientProfileScreen = React.lazy(
  () => import("pages/Client/ClientProfileScreen"),
);

const AddScheduleScreen = React.lazy(
  () => import("pages/Schedule/AddScheduleScreen"),
);
const PriceScheduleScreen = React.lazy(
  () => import("pages/Schedule/PriceScheduleScreen"),
);

const ScheduleStatsScreen = React.lazy(
  () => import("pages/Schedule/ScheduleStatsScreen"),
);

// const OfflineReservationScreen = React.lazy(
//   () => import("pages/Reservation/OfflineReservationScreen")
// );
const ReservationListScreen = React.lazy(
  () => import("pages/Reservation/ReservationListScreen"),
);
const ReservationTableScreen = React.lazy(
  () => import("pages/Reservation/ReservationTableScreen"),
);

const AddStaffScreen = React.lazy(() => import("pages/Staff/AddStaffScreen"));
const EditStaffScreen = React.lazy(() => import("pages/Staff/EditStaffScreen"));
const StaffListScreen = React.lazy(() => import("pages/Staff/StaffListScreen"));
const StaffListAllScreen = React.lazy(
  () => import("pages/Staff/StaffListScreen"),
);

const AddRoleScreen = React.lazy(() => import("pages/Role/AddRoleScreen"));
const EditRoleScreen = React.lazy(() => import("pages/Role/EditRoleScreen"));
const SettingRoleScreen = React.lazy(
  () => import("pages/Role/SettingRoleScreen"),
);

const MembershipEditScreen = React.lazy(
  () => import("pages/Membership/MembershipEditScreen"),
);
const MembershipListScreen = React.lazy(
  () => import("pages/Membership/MembershipListScreen"),
);

const CustomerListScreen = React.lazy(
  () => import("pages/Customer/CustomerListScreen"),
);
const CustomerEditScreen = React.lazy(
  () => import("pages/Customer/CustomerEditScreen"),
);

const CourseInfoListScreen = React.lazy(
  () => import("pages/Information/CourseInfoListScreen"),
);

const CourseInfoEditScreen = React.lazy(
  () => import("pages/Information/CourseInfoEditScreen"),
);

const TeeTimeInfoListScreen = React.lazy(
  () => import("pages/Information/TeeTimeInfoListScreen"),
);

const TeeTimeInfoEditScreen = React.lazy(
  () => import("pages/Information/TeeTimeInfoEditScreen"),
);

const PriceInfoListScreen = React.lazy(
  () => import("pages/Information/PriceInfoListScreen"),
);

const PriceInfoEditScreen = React.lazy(
  () => import("pages/Information/PriceInfoEditScreen"),
);

const ChargeInfoListScreen = React.lazy(
  () => import("pages/Charges/ChargeInfoListScreen"),
);

const ChargeInfoEditScreen = React.lazy(
  () => import("pages/Charges/ChargeInfoEditScreen"),
);

const ChargeCategoryEditScreen = React.lazy(
  () => import("pages/Charges/ChargeCategoryEditScreen"),
);

const ForwardScreen = React.lazy(() => import("pages/Forward/ForwardScreen"));

function App() {
  const [isSideBar, setIsSideBar] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [club, setClub] = useState(sessionStorage.getItem("clubId") || "");
  window.setClub = setClub;

  useEffect(() => {
    if (
      [
        "/",
        "/mobile-view",
        "/reset-password",
        "/verify-password",
        "/select-account-type",
        "/sign-up-business",
        "/sign-up-employee",
        "/welcome",
        "/home-error",
        "/client-profile",
        "/reservation-detail",
        "/signup/verify",
        "/error",
        "/forward",
        "/email-verification",
      ].includes(location.pathname) ||
      location.pathname.includes("/profile") ||
      location.pathname.includes("/resetpw")
    ) {
      setIsSideBar(false);
    } else {
      setIsSideBar(true);
    }
  }, [location, location.pathname, navigate]);

  const { accessLoaded, roleName, access, clubName, clubImageRef } =
    useAccessManager(club, location.pathname, location.search);

  const isHomePage = ["/home", "/staff-all"].includes(location.pathname);

  return (
    <div className="App">
      <link
        rel="stylesheet"
        href="https://unpkg.com/leaflet@1.0.1/dist/leaflet.css"
      />
      <GlobalProvider>
        <Suspense>
          <ModalProvider ref={modalRef}>
            {isSideBar && (
              <SideBar
                clubImageRef={clubImageRef}
                access={access}
                roleName={roleName}
                clubName={clubName}
                clubId={club}
                isProfile={!isHomePage && clubImageRef !== null}
              />
            )}
            <main>
              {location.pathname !== "/mobile-view" && (
                <Header
                  isStatic={
                    !["/mobile-view", "/welcome"].includes(location.pathname) &&
                    isSideBar
                  }
                />
              )}
              <MobilePage />
              <PanelProvider>
                <AccessProvider
                  isLoaded={accessLoaded}
                  hasToken={!!sessionStorage.getItem("signToken")}>
                  <Routes key={location.key}>
                    <Route path="/" element={<Navigate to="/welcome" />} />
                    <Route path="/welcome" element={<WelcomeMapScreen />} />
                    <Route
                      path="/home"
                      element={
                        <ProtectedRoute hasAccess={access.home}>
                          <HomeScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/home-error" element={<HomeErrorScreen />} />
                    <Route
                      path="/email-verification"
                      element={<EmailVerificationScreen />}
                    />
                    {/* <Route path="/" element={<LoginScreen />} /> */}
                    <Route path="/reset-password" element={<ResetPwScreen />} />
                    <Route path="/resetpw" element={<ToResetPwScreen />} />
                    <Route
                      path="/verify-password"
                      element={<VerifyPasswordScreen />}
                    />
                    <Route
                      path="/select-account-type"
                      element={<SelectTypeScreen />}
                    />
                    <Route
                      path="/sign-up-business"
                      element={<SignUpBusinessScreen />}
                    />
                    <Route
                      path="/sign-up-employee"
                      element={<SignUpEmployeeScreen />}
                    />

                    {/* Management */}
                    {/* Club information */}
                    <Route path="/add-golf-club" element={<GolfInfoScreen />} />
                    <Route
                      path="/golf-info"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <GolfInfoScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Course info */}
                    <Route
                      path="/course-list"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <CourseInfoListScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/course-create"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <CourseInfoEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/course-update"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <CourseInfoEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tee time */}
                    <Route
                      path="/tee-time"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <TeeTimeInfoListScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tee-time-create"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <TeeTimeInfoEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tee-time-update"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <TeeTimeInfoEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Pricing */}
                    <Route
                      path="/price-list"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <PriceInfoListScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/price-create"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <PriceInfoEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/price-update"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <PriceInfoEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Charges */}
                    <Route
                      path="/charge-list"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <ChargeInfoListScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/charge-edit"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <ChargeInfoEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/charge-category-edit"
                      element={
                        <ProtectedRoute hasAccess={access.club}>
                          <ChargeCategoryEditScreen />
                        </ProtectedRoute>
                      }
                    />

                    {/* Reservation */}
                    {/* Insights */}
                    <Route
                      path="/Insights"
                      element={
                        <ProtectedRoute hasAccess={access.reservation}>
                          <ScheduleStatsScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Open schedule */}
                    <Route
                      path="/add-schedule"
                      element={
                        <ProtectedRoute hasAccess={access.reservation}>
                          <AddScheduleScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/price-schedule"
                      element={
                        <ProtectedRoute hasAccess={access.reservation}>
                          <PriceScheduleScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tee time table */}
                    <Route
                      path="/reservation-table"
                      element={
                        <ProtectedRoute hasAccess={access.reservation}>
                          <ReservationTableScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Reservation list */}
                    <Route
                      path="/reservation"
                      element={
                        <ProtectedRoute hasAccess={access.reservation}>
                          <ReservationListScreen />
                        </ProtectedRoute>
                      }
                    />

                    {/* Sales */}
                    {/* Sales analytics */}
                    <Route
                      path="/sales"
                      element={
                        <ProtectedRoute hasAccess={access.sales}>
                          <SalesChartScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Sales details */}
                    <Route
                      path="/sales-detail"
                      element={
                        <ProtectedRoute hasAccess={access.sales}>
                          <SalesDetailListScreen />
                        </ProtectedRoute>
                      }
                    />

                    {/* Customer */}
                    {/* Membership Tiers */}
                    <Route
                      path="/membership-tiers"
                      element={
                        <ProtectedRoute hasAccess={access.membership}>
                          <MembershipListScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/membership-create"
                      element={
                        <ProtectedRoute hasAccess={access.membership}>
                          <MembershipEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/membership-update"
                      element={
                        <ProtectedRoute hasAccess={access.membership}>
                          <MembershipEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Customer list */}
                    <Route
                      path="/customer-list"
                      element={
                        <ProtectedRoute hasAccess={access.membership}>
                          <CustomerListScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/customer-create"
                      element={
                        <ProtectedRoute hasAccess={access.membership}>
                          <CustomerEditScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/customer-update"
                      element={
                        <ProtectedRoute hasAccess={access.membership}>
                          <CustomerEditScreen />
                        </ProtectedRoute>
                      }
                    />

                    {/* Staff */}
                    {/* Permissions */}
                    <Route
                      path="/setting-role"
                      element={
                        <ProtectedRoute hasAccess={access.staff}>
                          <SettingRoleScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/add-role"
                      element={
                        <ProtectedRoute hasAccess={access.staff}>
                          <AddRoleScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/edit-role"
                      element={
                        <ProtectedRoute hasAccess={access.staff}>
                          <EditRoleScreen />
                        </ProtectedRoute>
                      }
                    />
                    {/* Staff list */}
                    <Route
                      path="/staff"
                      element={
                        <ProtectedRoute hasAccess={access.staff}>
                          <StaffListScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/add-staff"
                      element={
                        <ProtectedRoute hasAccess={access.staff}>
                          <AddStaffScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/edit-staff"
                      element={
                        <ProtectedRoute hasAccess={access.staff}>
                          <EditStaffScreen />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/staff-all"
                      element={
                        <ProtectedRoute hasAccess={access.staffAll}>
                          <StaffListAllScreen />
                        </ProtectedRoute>
                      }
                    />

                    {/* Client */}
                    <Route
                      path="/client"
                      element={
                        <ProtectedRoute hasAccess={access.client}>
                          <ClientListScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/client-profile"
                      element={
                        <ProtectedRoute hasAccess={access.client}>
                          <ClientProfileScreen />
                        </ProtectedRoute>
                      }
                    />

                    <Route path="/profile" element={<MyProfileScreen />} />

                    <Route path="/signup/verify" element={<VerifiedEmail />} />

                    {/* <Route
                  path="/offline-reservation"
                  element={<OfflineReservationScreen />}
                /> */}

                    <Route path="/forward" element={<ForwardScreen />} />
                    <Route path="*" element={<Navigate to="/error" />} />
                    <Route path="/error" element={<ErrorScreen />} />
                    <Route path="/designs" element={<DesignPage />} />
                  </Routes>
                </AccessProvider>
              </PanelProvider>
            </main>
          </ModalProvider>
        </Suspense>
      </GlobalProvider>
    </div>
  );
}

export default App;
